/**
 * Enterprise Messaging Page.
 *
 * Three-column layout: sidebar (channels + DMs) | messages | thread panel.
 * Features: public/private channels, DMs, threads, editing, pinning,
 * reactions, unread tracking, presence indicators, member management,
 * search, and inline channel creation.
 */

import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Hash,
  Lock,
  MessageSquare,
  Plus,
  Send,
  Search,
  Pin,
  Edit2,
  Trash2,
  X,
  Users,
  MessageCircle,
  Archive,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/stores/toastStore";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import type { PaginatedResponse } from "@/types/common";

// =========================================================================
// Types
// =========================================================================

interface Channel {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  topic: string | null;
  channel_type: string;
  members: string[];
  is_archived: boolean;
  last_message_at: string | null;
  message_count: number;
  created_by: string | null;
  created_at: string;
  unread_count: number;
}

interface Msg {
  id: string;
  channel_id: string;
  sender_id: string;
  sender_name: string;
  content: string;
  parent_id: string | null;
  reply_count: number;
  is_edited: boolean;
  edited_at: string | null;
  is_pinned: boolean;
  is_deleted: boolean;
  reactions: Record<string, string[]> | null;
  created_at: string;
}

interface OrgUser {
  user_id: string;
  full_name: string;
  is_online: boolean;
  last_seen_at: string | null;
}

// =========================================================================
// Main Page
// =========================================================================

function MessagesPage() {
  const { channelId: routeChannelId } = useParams<{ channelId: string }>();
  const { user } = useAuth();
  const qc = useQueryClient();

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    routeChannelId || null,
  );
  const [threadParentId, setThreadParentId] = useState<string | null>(null);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState("");

  // Update presence every 60 seconds.
  useEffect(() => {
    const ping = () =>
      apiClient.post("/api/v1/messaging/presence").catch(() => {});
    ping();
    const interval = setInterval(ping, 60000);
    return () => clearInterval(interval);
  }, []);

  // Channels.
  const { data: channels } = useQuery<Channel[]>({
    queryKey: ["channels"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/messaging/channels")).data,
    refetchInterval: 10000,
  });

  // Org users (for DM picker).
  const { data: orgUsers } = useQuery<OrgUser[]>({
    queryKey: ["messaging-users"],
    queryFn: async () => (await apiClient.get("/api/v1/messaging/users")).data,
  });

  const publicChannels = (channels || []).filter(
    (c) => c.channel_type === "public" || c.channel_type === "private",
  );
  const dmChannels = (channels || []).filter(
    (c) => c.channel_type === "direct",
  );

  const filtered = sidebarSearch
    ? (channels || []).filter((c) =>
        c.name.toLowerCase().includes(sidebarSearch.toLowerCase()),
      )
    : null;

  const selectedChannel = (channels || []).find(
    (c) => c.id === selectedChannelId,
  );

  // Mark channel as read when selected.
  useEffect(() => {
    if (selectedChannelId) {
      apiClient
        .post(`/api/v1/messaging/channels/${selectedChannelId}/read`)
        .catch(() => {});
      qc.invalidateQueries({ queryKey: ["channels"] });
    }
  }, [selectedChannelId, qc]);

  // Create channel.
  const [newChName, setNewChName] = useState("");
  const [newChDesc, setNewChDesc] = useState("");
  const [newChType, setNewChType] = useState("public");
  const createChannelMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/messaging/channels", {
          name: newChName,
          description: newChDesc || undefined,
          channel_type: newChType,
        })
      ).data as Channel,
    onSuccess: (ch) => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      setSelectedChannelId(ch.id);
      setShowNewChannel(false);
      setNewChName("");
      setNewChDesc("");
      setNewChType("public");
      toast.success("Channel created", `#${ch.name} is ready`);
    },
    onError: () => toast.error("Failed to create channel"),
  });

  // Create DM.
  const createDmMutation = useMutation({
    mutationFn: async (userId: string) =>
      (await apiClient.post("/api/v1/messaging/dm", { user_id: userId }))
        .data as Channel,
    onSuccess: (ch) => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      setSelectedChannelId(ch.id);
      setShowNewDm(false);
    },
    onError: () => toast.error("Failed to start conversation"),
  });

  // Get display name for DM (show other person's name, not "Me, Other").
  const getDmDisplayName = (ch: Channel) => {
    if (!user) return ch.name;
    const names = ch.name.split(", ");
    return names.find((n) => n !== user.full_name) || ch.name;
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-0 -m-4 lg:-m-6">
      {/* ═══ Sidebar ═══ */}
      <div className="w-64 shrink-0 flex flex-col border-r border-[var(--border-subtle)] bg-[var(--surface-base)]">
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">
            Messages
          </h2>
          <div className="flex gap-1">
            <button
              onClick={() => {
                setShowNewDm(true);
                setShowNewChannel(false);
              }}
              title="New direct message"
              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => {
                setShowNewChannel(true);
                setShowNewDm(false);
              }}
              title="New channel"
              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Sidebar search */}
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            <input
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              placeholder="Search channels..."
              className="w-full h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] pl-8 pr-3 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
            />
          </div>
        </div>

        {/* Channel list */}
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {filtered ? (
            /* Search results */
            <div className="space-y-0.5">
              {filtered.map((ch) => (
                <ChannelButton
                  key={ch.id}
                  channel={ch}
                  isActive={ch.id === selectedChannelId}
                  onClick={() => {
                    setSelectedChannelId(ch.id);
                    setSidebarSearch("");
                  }}
                  dmName={
                    ch.channel_type === "direct"
                      ? getDmDisplayName(ch)
                      : undefined
                  }
                />
              ))}
              {filtered.length === 0 && (
                <p className="px-2 py-4 text-xs text-[var(--text-tertiary)] text-center">
                  No matches
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Channels section */}
              <div className="mb-2">
                <p className="px-2 py-1.5 text-[9px] font-medium uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                  Channels
                </p>
                <div className="space-y-0.5">
                  {publicChannels.map((ch) => (
                    <ChannelButton
                      key={ch.id}
                      channel={ch}
                      isActive={ch.id === selectedChannelId}
                      onClick={() => setSelectedChannelId(ch.id)}
                    />
                  ))}
                  {publicChannels.length === 0 && (
                    <p className="px-2 py-2 text-[11px] text-[var(--text-tertiary)]">
                      No channels yet
                    </p>
                  )}
                </div>
              </div>

              {/* DMs section */}
              <div>
                <p className="px-2 py-1.5 text-[9px] font-medium uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                  Direct messages
                </p>
                <div className="space-y-0.5">
                  {dmChannels.map((ch) => (
                    <ChannelButton
                      key={ch.id}
                      channel={ch}
                      isActive={ch.id === selectedChannelId}
                      onClick={() => setSelectedChannelId(ch.id)}
                      dmName={getDmDisplayName(ch)}
                    />
                  ))}
                  {dmChannels.length === 0 && (
                    <p className="px-2 py-2 text-[11px] text-[var(--text-tertiary)]">
                      No conversations yet
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══ Main Chat Area ═══ */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* New Channel Dialog */}
        {showNewChannel && (
          <div className="border-b border-[var(--border-default)] bg-[var(--surface-base)] px-4 py-3 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                Create a channel
              </h3>
              <button
                onClick={() => setShowNewChannel(false)}
                className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createChannelMutation.mutate();
              }}
              className="grid grid-cols-1 gap-3 sm:grid-cols-3"
            >
              <Input
                label="Channel name"
                value={newChName}
                onChange={(e) => setNewChName(e.target.value)}
                required
                placeholder="e.g. shop-floor"
              />
              <Input
                label="Description"
                value={newChDesc}
                onChange={(e) => setNewChDesc(e.target.value)}
                placeholder="What's this channel about?"
              />
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)]">
                  Visibility
                </label>
                <div className="mt-1.5 flex gap-2">
                  {(["public", "private"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setNewChType(t)}
                      className={`flex items-center gap-1.5 rounded-[var(--radius-md)] border px-3 py-1.5 text-xs font-medium transition-all ${
                        newChType === t
                          ? "border-[var(--color-interactive-primary)]/30 bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                          : "border-[var(--border-default)] text-[var(--text-secondary)]"
                      }`}
                    >
                      {t === "public" ? (
                        <Hash className="h-3 w-3" />
                      ) : (
                        <Lock className="h-3 w-3" />
                      )}
                      {t === "public" ? "Public" : "Private"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-3 flex justify-end">
                <Button
                  type="submit"
                  size="sm"
                  isLoading={createChannelMutation.isPending}
                >
                  Create channel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* New DM Dialog */}
        {showNewDm && (
          <div className="border-b border-[var(--border-default)] bg-[var(--surface-base)] px-4 py-3 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                New direct message
              </h3>
              <button
                onClick={() => setShowNewDm(false)}
                className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {orgUsers
                ?.filter((u) => u.user_id !== user?.id)
                .map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => createDmMutation.mutate(u.user_id)}
                    className="flex w-full items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-left hover:bg-[var(--surface-base)] transition-colors"
                  >
                    <div className="relative">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[10px] font-medium text-[var(--text-secondary)]">
                        {u.full_name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                      {u.is_online && (
                        <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[var(--text-success)] border-2 border-[var(--surface-base)]" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">
                        {u.full_name}
                      </p>
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        {u.is_online ? "Online" : "Offline"}
                      </p>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Chat view or empty state */}
        {selectedChannel ? (
          <ChatView
            channel={selectedChannel}
            currentUserId={user?.id || ""}
            onOpenThread={(msgId) => setThreadParentId(msgId)}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center bg-[var(--surface-sunken)]">
            <div className="text-center">
              <MessageSquare className="mx-auto h-12 w-12 text-[var(--text-tertiary)]" />
              <h3 className="mt-3 text-sm font-medium text-[var(--text-primary)]">
                Select a conversation
              </h3>
              <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                Choose a channel or start a direct message
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ═══ Thread Panel ═══ */}
      {threadParentId && selectedChannelId && (
        <ThreadPanel
          channelId={selectedChannelId}
          parentId={threadParentId}
          onClose={() => setThreadParentId(null)}
        />
      )}
    </div>
  );
}

// =========================================================================
// Channel Sidebar Button
// =========================================================================

function ChannelButton({
  channel,
  isActive,
  onClick,
  dmName,
}: {
  channel: Channel;
  isActive: boolean;
  onClick: () => void;
  dmName?: string;
}) {
  const Icon =
    channel.channel_type === "direct"
      ? MessageSquare
      : channel.channel_type === "private"
        ? Lock
        : Hash;
  const displayName = dmName || channel.name;

  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-left transition-all ${
        isActive
          ? "bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
          : "text-[var(--text-secondary)] hover:bg-[var(--surface-base)] hover:text-[var(--text-primary)]"
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
      <span className="flex-1 truncate text-[13px] font-medium">
        {displayName}
      </span>
      {channel.unread_count > 0 && (
        <span className="shrink-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-interactive-primary)] text-[10px] font-semibold text-white px-1.5">
          {channel.unread_count > 99 ? "99+" : channel.unread_count}
        </span>
      )}
    </button>
  );
}

// =========================================================================
// Chat View (Main Message Area)
// =========================================================================

function ChatView({
  channel,
  currentUserId,
  onOpenThread,
}: {
  channel: Channel;
  currentUserId: string;
  onOpenThread: (parentId: string) => void;
}) {
  const qc = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery<PaginatedResponse<Msg>>({
    queryKey: ["messages", channel.id],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/messaging/channels/${channel.id}/messages?page_size=100`,
        )
      ).data,
    refetchInterval: 4000,
  });

  const [input, setInput] = useState("");
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  const sendMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/messaging/channels/${channel.id}/messages`,
          { content: input },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", channel.id] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      setInput("");
    },
  });

  const editMutation = useMutation({
    mutationFn: async (msgId: string) =>
      (
        await apiClient.put(
          `/api/v1/messaging/channels/${channel.id}/messages/${msgId}`,
          { content: editContent },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", channel.id] });
      setEditingMsgId(null);
      setEditContent("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (msgId: string) => {
      await apiClient.delete(
        `/api/v1/messaging/channels/${channel.id}/messages/${msgId}`,
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["messages", channel.id] }),
  });

  const pinMutation = useMutation({
    mutationFn: async (msgId: string) =>
      (
        await apiClient.post(
          `/api/v1/messaging/channels/${channel.id}/messages/${msgId}/pin`,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["messages", channel.id] }),
  });

  const reactMutation = useMutation({
    mutationFn: async ({ msgId, emoji }: { msgId: string; emoji: string }) =>
      (
        await apiClient.post(
          `/api/v1/messaging/channels/${channel.id}/messages/${msgId}/react`,
          { emoji },
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["messages", channel.id] }),
  });

  const messages = [...(data?.items || [])].reverse();

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim()) sendMutation.mutate();
    }
  };

  const quickReactions = ["👍", "❤️", "🎉", "👀", "🔥"];

  return (
    <>
      {/* Channel header */}
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--surface-base)] px-4 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {channel.channel_type === "private" ? (
              <Lock className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            ) : channel.channel_type !== "direct" ? (
              <Hash className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            ) : null}
            <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">
              {channel.name}
            </h3>
            {channel.members.length > 0 && (
              <span className="text-[11px] text-[var(--text-tertiary)]">
                {channel.members.length} members
              </span>
            )}
          </div>
          {channel.topic && (
            <p className="text-[11px] text-[var(--text-tertiary)] truncate mt-0.5">
              {channel.topic}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            title="Pinned messages"
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
          >
            <Pin className="h-3.5 w-3.5" />
          </button>
          <button
            title="Members"
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
          >
            <Users className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 bg-[var(--surface-sunken)]">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <Hash className="mx-auto h-10 w-10 text-[var(--text-tertiary)]" />
              <h4 className="mt-2 text-sm font-medium text-[var(--text-primary)]">
                {channel.channel_type === "direct"
                  ? "Start a conversation"
                  : `Welcome to #${channel.name}`}
              </h4>
              <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                {channel.description ||
                  "Send the first message to get things started."}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((msg, i) => {
              const prevMsg = messages[i - 1];
              const showHeader =
                !prevMsg ||
                prevMsg.sender_id !== msg.sender_id ||
                new Date(msg.created_at).getTime() -
                  new Date(prevMsg.created_at).getTime() >
                  300000;

              return (
                <div
                  key={msg.id}
                  className={`group relative rounded-[var(--radius-md)] px-3 transition-colors hover:bg-[var(--surface-base)] ${msg.is_pinned ? "border-l-2 border-[var(--text-warning)] bg-[var(--fill-warning)]/30" : ""}`}
                >
                  {showHeader && (
                    <div className="flex items-baseline gap-2 pt-2">
                      <span className="text-[13px] font-semibold text-[var(--text-primary)]">
                        {msg.sender_name}
                      </span>
                      <span className="text-[10px] text-[var(--text-tertiary)]">
                        {new Date(msg.created_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                      {msg.is_edited && (
                        <span className="text-[9px] text-[var(--text-tertiary)] italic">
                          (edited)
                        </span>
                      )}
                      {msg.is_pinned && (
                        <Pin className="h-3 w-3 text-[var(--text-warning)] inline" />
                      )}
                    </div>
                  )}

                  {/* Message content */}
                  {editingMsgId === msg.id ? (
                    <div className="py-1">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        rows={2}
                        className="w-full rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
                        autoFocus
                      />
                      <div className="flex gap-1 mt-1">
                        <button
                          onClick={() => editMutation.mutate(msg.id)}
                          className="text-xs text-[var(--color-interactive-primary)] font-medium hover:underline"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingMsgId(null)}
                          className="text-xs text-[var(--text-tertiary)] hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p
                      className={`text-sm leading-relaxed ${msg.is_deleted ? "italic text-[var(--text-tertiary)]" : "text-[var(--text-primary)]"} ${showHeader ? "pb-1" : "py-0.5"}`}
                    >
                      {msg.content}
                    </p>
                  )}

                  {/* Reactions */}
                  {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                    <div className="flex flex-wrap gap-1 pb-1">
                      {Object.entries(msg.reactions).map(([emoji, userIds]) => (
                        <button
                          key={emoji}
                          onClick={() =>
                            reactMutation.mutate({ msgId: msg.id, emoji })
                          }
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
                            userIds.includes(currentUserId)
                              ? "border-[var(--color-interactive-primary)]/30 bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                              : "border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-base)]"
                          }`}
                        >
                          <span>{emoji}</span>
                          <span className="font-medium">{userIds.length}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Thread indicator */}
                  {msg.reply_count > 0 && (
                    <button
                      onClick={() => onOpenThread(msg.id)}
                      className="flex items-center gap-1.5 text-xs text-[var(--color-interactive-primary)] font-medium hover:underline pb-1"
                    >
                      <MessageCircle className="h-3 w-3" />
                      {msg.reply_count}{" "}
                      {msg.reply_count === 1 ? "reply" : "replies"}
                    </button>
                  )}

                  {/* Hover action bar */}
                  {!msg.is_deleted && editingMsgId !== msg.id && (
                    <div className="absolute -top-3 right-2 hidden group-hover:flex items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] shadow-sm px-1 py-0.5">
                      {quickReactions.map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() =>
                            reactMutation.mutate({ msgId: msg.id, emoji })
                          }
                          className="h-6 w-6 flex items-center justify-center rounded hover:bg-[var(--surface-base)] text-sm"
                        >
                          {emoji}
                        </button>
                      ))}
                      <div className="w-px h-4 bg-[var(--border-default)] mx-0.5" />
                      <button
                        onClick={() => onOpenThread(msg.id)}
                        title="Reply in thread"
                        className="h-6 w-6 flex items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)]"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => pinMutation.mutate(msg.id)}
                        title={msg.is_pinned ? "Unpin" : "Pin"}
                        className="h-6 w-6 flex items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)]"
                      >
                        <Pin className="h-3.5 w-3.5" />
                      </button>
                      {msg.sender_id === currentUserId && (
                        <>
                          <button
                            onClick={() => {
                              setEditingMsgId(msg.id);
                              setEditContent(msg.content);
                            }}
                            title="Edit"
                            className="h-6 w-6 flex items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)]"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => deleteMutation.mutate(msg.id)}
                            title="Delete"
                            className="h-6 w-6 flex items-center justify-center rounded text-[var(--text-tertiary)] hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Composer */}
      {!channel.is_archived ? (
        <div className="border-t border-[var(--border-subtle)] bg-[var(--surface-base)] px-4 py-3">
          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  channel.channel_type === "direct"
                    ? `Message ${channel.name.split(", ").find((n) => n !== "You") || ""}`
                    : `Message #${channel.name}`
                }
                rows={1}
                className="w-full resize-none rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] min-h-[36px] max-h-32"
                style={{ height: "auto" }}
                onInput={(e) => {
                  const t = e.target as HTMLTextAreaElement;
                  t.style.height = "auto";
                  t.style.height = Math.min(t.scrollHeight, 128) + "px";
                }}
              />
            </div>
            <Button
              size="sm"
              onClick={() => {
                if (input.trim()) sendMutation.mutate();
              }}
              isLoading={sendMutation.isPending}
              disabled={!input.trim()}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">
            <kbd className="rounded border border-[var(--border-default)] px-1 py-0.5 text-[9px] font-mono">
              Enter
            </kbd>{" "}
            to send ·{" "}
            <kbd className="rounded border border-[var(--border-default)] px-1 py-0.5 text-[9px] font-mono">
              Shift+Enter
            </kbd>{" "}
            for new line
          </p>
        </div>
      ) : (
        <div className="border-t border-[var(--border-subtle)] bg-[var(--fill-warning)] px-4 py-3 text-center">
          <p className="text-xs text-[var(--text-warning)] font-medium">
            <Archive className="h-3.5 w-3.5 inline mr-1" /> This channel is
            archived
          </p>
        </div>
      )}
    </>
  );
}

// =========================================================================
// Thread Panel
// =========================================================================

function ThreadPanel({
  channelId,
  parentId,
  onClose,
}: {
  channelId: string;
  parentId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const { data: parentMsg } = useQuery<Msg | null>({
    queryKey: ["thread-parent", channelId, parentId],
    queryFn: async () => {
      const result = await apiClient.get(
        `/api/v1/messaging/channels/${channelId}/messages?page_size=100`,
      );
      const all = (result.data as PaginatedResponse<Msg>).items;
      return all.find((m) => m.id === parentId) || null;
    },
  });

  const { data: replies } = useQuery<Msg[]>({
    queryKey: ["thread", channelId, parentId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/messaging/channels/${channelId}/messages/${parentId}/thread`,
        )
      ).data,
    refetchInterval: 4000,
  });

  const [input, setInput] = useState("");
  const replyMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/messaging/channels/${channelId}/messages`,
          {
            content: input,
            parent_id: parentId,
          },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["thread", channelId, parentId] });
      qc.invalidateQueries({ queryKey: ["messages", channelId] });
      setInput("");
    },
  });

  return (
    <div className="w-80 shrink-0 flex flex-col border-l border-[var(--border-subtle)] bg-[var(--surface-base)] animate-slide-in-left">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-2.5">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          Thread
        </h3>
        <button
          onClick={onClose}
          className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Parent message */}
      {parentMsg && (
        <div className="border-b border-[var(--border-subtle)] px-4 py-3">
          <div className="flex items-baseline gap-2">
            <span className="text-[13px] font-semibold text-[var(--text-primary)]">
              {parentMsg.sender_name}
            </span>
            <span className="text-[10px] text-[var(--text-tertiary)]">
              {new Date(parentMsg.created_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          </div>
          <p className="text-sm text-[var(--text-primary)] mt-0.5">
            {parentMsg.content}
          </p>
          <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
            {parentMsg.reply_count}{" "}
            {parentMsg.reply_count === 1 ? "reply" : "replies"}
          </p>
        </div>
      )}

      {/* Thread replies */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
        {(replies || []).map((msg) => (
          <div key={msg.id}>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold text-[var(--text-primary)]">
                {msg.sender_name}
              </span>
              <span className="text-[10px] text-[var(--text-tertiary)]">
                {new Date(msg.created_at).toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <p className="text-sm text-[var(--text-primary)]">{msg.content}</p>
          </div>
        ))}
        {(replies || []).length === 0 && (
          <p className="text-center text-xs text-[var(--text-tertiary)] py-4">
            No replies yet
          </p>
        )}
      </div>

      {/* Reply composer */}
      <div className="border-t border-[var(--border-subtle)] px-3 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (input.trim()) replyMutation.mutate();
          }}
          className="flex gap-1.5"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Reply..."
            className="flex-1 h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
          />
          <Button
            size="sm"
            type="submit"
            isLoading={replyMutation.isPending}
            disabled={!input.trim()}
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </form>
      </div>
    </div>
  );
}

export default MessagesPage;
